import React from "react";
import {
  CheckSquare,
  Square,
  SquareMinus,
  Eye,
  Edit3,
  AlertTriangle,
  Thermometer,
  Printer,
  CheckCircle2,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import {
  StatusBadge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  LoadingOverlay,
} from "@/shared/ui";
import { cn } from "@/shared/lib/cn";
import type { TestUiListItem } from "../model/testUiData";

export interface TestUiListTableProps {
  isMobilePreview: boolean;
  filteredRows: TestUiListItem[];
  selectedIds: string[];
  loading?: boolean;
  rowNumberStart?: number;
  onToggleSelectAll: () => void;
  onToggleSelectRow: (id: string, e: React.MouseEvent) => void;
  onSelectRow: (item: TestUiListItem) => void;
  onOpenCreate: () => void;
  onClearSelection: () => void;
}

export function TestUiListTable({
  isMobilePreview,
  filteredRows,
  selectedIds,
  loading = false,
  rowNumberStart = 1,
  onToggleSelectAll,
  onToggleSelectRow,
  onSelectRow,
  onOpenCreate,
  onClearSelection,
}: TestUiListTableProps) {
  return (
    <div className="space-y-3">
      {/* Bulk Actions Floating Notification if selected */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-primary-soft px-3 py-1.5 text-caption font-medium text-primary-fg animate-in fade-in duration-150">
          <div className="flex items-center gap-2">
            <span className="flex size-5 items-center justify-center rounded-full bg-primary text-on-primary text-caption font-bold">
              {selectedIds.length}
            </span>
            <span>mục đã được chọn để thực hiện thao tác hàng loạt</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 h-[28px] px-2.5 text-caption font-medium rounded-md bg-surface border border-border text-text hover:bg-bg cursor-pointer"
            >
              <Printer className="size-3" /> In thẻ kho / Tem lô
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 h-[28px] px-2.5 text-caption font-medium rounded-md bg-primary text-on-primary hover:bg-primary-hover cursor-pointer"
            >
              <CheckCircle2 className="size-3" /> Duyệt nhanh
            </button>
            <button
              type="button"
              onClick={onClearSelection}
              className="text-caption font-semibold text-primary hover:underline ml-2 cursor-pointer"
            >
              Bỏ chọn
            </button>
          </div>
        </div>
      )}

      {isMobilePreview ? (
        /* Mobile Card Stack Pattern */
        <div className="relative space-y-3">
          <LoadingOverlay
            visible={loading}
            label="Đang tải trang..."
            className="z-20 rounded-md"
          />
          <div className={cn("space-y-3 transition-opacity duration-200", loading && "opacity-50 pointer-events-none")}>
            <div className="rounded-md bg-warning-soft px-3 py-2 text-caption text-warning-fg border border-warning/30 flex items-center gap-2">
              <AlertTriangle className="size-4 shrink-0" />
              <span>Đang ở chế độ mô phỏng Mobile: Card chạm để mở chi tiết, menu ⋯ thao tác nhanh.</span>
            </div>
            {filteredRows.map((row) => (
            <div
              key={row.id}
              onClick={() => onSelectRow(row)}
              className="cursor-pointer rounded-md border border-border bg-surface p-3.5 shadow-2xs transition hover:border-primary active:bg-bg"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="font-mono text-caption font-bold text-primary">
                    {row.code}
                  </span>
                  <h3 className="m-0 mt-0.5 text-body font-semibold text-text">
                    {row.productName}
                  </h3>
                  <p className="m-0 text-caption text-text-muted">
                    {row.partnerName}
                  </p>
                </div>
                <div
                  className="flex items-center gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <StatusBadge status={row.status} />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        aria-label={`Thao tác ${row.code}`}
                        title="Thao tác"
                        className="inline-flex size-7 items-center justify-center rounded-control bg-transparent text-text-secondary hover:text-primary transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]"
                      >
                        <MoreHorizontal className="size-4 pointer-events-none" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[12rem] shadow-lg py-1">
                      <DropdownMenuItem
                        className="cursor-pointer gap-2.5 px-3 py-2 text-caption font-medium hover:bg-primary-soft/50"
                        onSelect={() => onSelectRow(row)}
                      >
                        <Eye className="size-3.5 text-text-muted" />
                        <span>Xem chi tiết</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer gap-2.5 px-3 py-2 text-caption font-medium hover:bg-primary-soft/50"
                        onSelect={onOpenCreate}
                      >
                        <Edit3 className="size-3.5 text-text-muted" />
                        <span>Chỉnh sửa</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="cursor-pointer gap-2.5 px-3 py-2 text-caption font-medium hover:bg-primary-soft/50"
                        onSelect={() => {}}
                      >
                        <Printer className="size-3.5 text-text-muted" />
                        <span>In tem / Thẻ kho</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="cursor-pointer gap-2.5 px-3 py-2 text-caption font-medium text-danger-fg focus:bg-danger-soft focus:text-danger-fg hover:bg-danger-soft hover:text-danger-fg"
                        onSelect={() => {}}
                      >
                        <Trash2 className="size-3.5" />
                        <span>Hủy phiếu</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border/60 pt-2 text-caption">
                <div>
                  <span className="text-text-muted">Lô SX: </span>
                  <span className="font-mono font-medium text-text">{row.lotNo}</span>
                </div>
                <div className="text-right">
                  <span className="text-text-muted">Số lượng: </span>
                  <span className="font-mono font-bold text-text">
                    {row.quantity.toLocaleString("vi-VN")} {row.uom}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-info-fg">
                  <Thermometer className="size-3.5" />
                  <span>{row.storageTemp}</span>
                </div>
                <div className="text-right text-text-secondary">
                  Hạn: {row.dueDate}
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>
      ) : (
        /* Desktop Table Pattern WITH SIGNATURE table-head-primary */
        <div className="relative overflow-hidden rounded-control border border-border bg-surface shadow-xs">
          <LoadingOverlay
            visible={loading}
            label="Đang tải trang..."
            className="z-20"
          />
          <div className="overflow-x-auto min-w-full scrollbar-thin">
            <table className="w-full text-left text-body border-collapse min-w-[760px]">
              <thead className="table-head-primary sticky top-0 bg-primary text-on-primary select-none">
                <tr className="border-b border-primary-hover">
                  <th className="w-10 px-3 py-2.5 text-center align-middle text-on-primary">
                    <button
                      type="button"
                      onClick={onToggleSelectAll}
                      className="inline-flex items-center justify-center align-middle leading-none text-on-primary/90 hover:text-on-primary cursor-pointer"
                      aria-label="Chọn tất cả"
                    >
                      {selectedIds.length > 0 && selectedIds.length === filteredRows.length ? (
                        <CheckSquare className="size-4" />
                      ) : selectedIds.length > 0 ? (
                        <SquareMinus className="size-4" />
                      ) : (
                        <Square className="size-4 opacity-70 hover:opacity-100" />
                      )}
                    </button>
                  </th>
                  <th className="w-12 px-3 py-2.5 text-center text-caption font-semibold tracking-wider uppercase text-on-primary">
                    #
                  </th>
                  <th className="w-20 px-3 py-2.5 text-center text-caption font-semibold tracking-wider uppercase text-on-primary whitespace-nowrap">
                    Thao tác
                  </th>
                  <th className="px-3 py-2.5 text-center text-caption font-semibold tracking-wider uppercase text-on-primary whitespace-nowrap">
                    Trạng thái
                  </th>
                  <th className="px-3 py-2.5 text-caption font-semibold tracking-wider uppercase text-on-primary whitespace-nowrap">
                    Mã phiếu
                  </th>
                  <th className="px-3 py-2.5 text-caption font-semibold tracking-wider uppercase text-on-primary whitespace-nowrap">
                    Sản phẩm / Dòng hàng
                  </th>
                  <th className="px-3 py-2.5 text-caption font-semibold tracking-wider uppercase text-on-primary whitespace-nowrap">
                    Đối tác / Phân xưởng
                  </th>
                  <th className="px-3 py-2.5 text-caption font-semibold tracking-wider uppercase text-on-primary whitespace-nowrap">
                    Lô & Điều kiện
                  </th>
                  <th className="px-3 py-2.5 text-right text-caption font-semibold tracking-wider uppercase text-on-primary whitespace-nowrap">
                    Số lượng
                  </th>
                  <th className="px-3 py-2.5 text-center text-caption font-semibold tracking-wider uppercase text-on-primary whitespace-nowrap">
                    Hạn xử lý (SLA)
                  </th>
                </tr>
              </thead>
              <tbody
                className={cn(
                  "divide-y divide-border/60 transition-opacity duration-200",
                  loading && "opacity-50 pointer-events-none"
                )}
              >
                {filteredRows.map((row, idx) => {
                  const isSelected = selectedIds.includes(row.id);
                  return (
                    <tr
                      key={row.id}
                      onClick={() => onSelectRow(row)}
                      className={cn(
                        "group cursor-pointer transition-colors",
                        isSelected
                          ? "bg-primary-soft"
                          : idx % 2 === 1
                            ? "bg-table-stripe"
                            : "bg-surface",
                        "hover:bg-primary-soft/60"
                      )}
                    >
                      {/* Checkbox */}
                      <td
                        className="px-3 py-2 text-center align-middle"
                        onClick={(e) => onToggleSelectRow(row.id, e)}
                      >
                        <button
                          type="button"
                          className="inline-flex items-center justify-center align-middle leading-none text-text-muted group-hover:text-primary cursor-pointer"
                          aria-label={`Chọn ${row.code}`}
                        >
                          {isSelected ? (
                            <CheckSquare className="size-4 text-primary" />
                          ) : (
                            <Square className="size-4 opacity-50 hover:opacity-100" />
                          )}
                        </button>
                      </td>

                      {/* STT */}
                      <td className="px-3 py-2 text-center font-mono text-caption text-text-muted">
                        {rowNumberStart + idx}
                      </td>

                      {/* Thao tác (Row actions gom thành icon ... với menu popup) */}
                      <td
                        className="px-3 py-2 text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              aria-label={`Thao tác phiếu ${row.code}`}
                              title="Thao tác"
                              className="inline-flex size-7 items-center justify-center rounded-control bg-transparent text-text-secondary hover:text-primary transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="size-4 pointer-events-none" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            className="min-w-[12rem] shadow-lg py-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <DropdownMenuItem
                              className="cursor-pointer gap-2.5 px-3 py-2 text-caption font-medium hover:bg-primary-soft/50"
                              onSelect={() => onSelectRow(row)}
                            >
                              <Eye className="size-3.5 text-text-muted" />
                              <span>Xem chi tiết</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="cursor-pointer gap-2.5 px-3 py-2 text-caption font-medium hover:bg-primary-soft/50"
                              onSelect={onOpenCreate}
                            >
                              <Edit3 className="size-3.5 text-text-muted" />
                              <span>Chỉnh sửa</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="cursor-pointer gap-2.5 px-3 py-2 text-caption font-medium hover:bg-primary-soft/50"
                              onSelect={() => {}}
                            >
                              <Printer className="size-3.5 text-text-muted" />
                              <span>In tem / Thẻ kho</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="cursor-pointer gap-2.5 px-3 py-2 text-caption font-medium text-danger-fg focus:bg-danger-soft focus:text-danger-fg hover:bg-danger-soft hover:text-danger-fg"
                              onSelect={() => {}}
                            >
                              <Trash2 className="size-3.5" />
                              <span>Hủy phiếu</span>
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>

                      {/* Trạng thái - Đứng ngay sau Thao tác */}
                      <td className="px-3 py-2 text-center whitespace-nowrap">
                        <StatusBadge status={row.status} />
                      </td>

                      {/* Mã phiếu */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="inline-block rounded bg-primary-soft/80 px-2 py-0.5 font-mono text-caption font-bold text-primary border border-primary/30 shadow-2xs">
                          {row.code}
                        </span>
                      </td>

                      {/* Sản phẩm */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="font-medium text-text">{row.productName}</div>
                        <div className="text-caption text-text-muted font-mono">
                          {row.productCode}
                        </div>
                      </td>

                      {/* Đối tác & Phân xưởng */}
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-text font-medium">{row.partnerName}</div>
                        <div className="text-caption text-text-muted">{row.plant}</div>
                      </td>

                      {/* Lô & Nhiệt độ */}
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-caption">
                        <div className="text-text font-semibold">{row.lotNo}</div>
                        <div className="flex items-center gap-1 text-info-fg">
                          <Thermometer className="size-3" />
                          <span>{row.storageTemp}</span>
                        </div>
                      </td>

                      {/* Số lượng */}
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <span className="font-mono font-bold tabular-nums text-text">
                          {row.quantity.toLocaleString("vi-VN")}
                        </span>{" "}
                        <span className="text-caption text-text-muted">{row.uom}</span>
                      </td>

                      {/* Hạn SLA */}
                      <td className="px-3 py-2 text-center whitespace-nowrap text-caption">
                        <span
                          className={cn(
                            row.priority === "Khẩn cấp"
                              ? "font-semibold text-danger-fg"
                              : "text-text-secondary"
                          )}
                        >
                          {row.dueDate}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
